/**
 * Tagada Rx — commission terms (marketplace mode).
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TableCard } from '@/components/ui/table-card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useAccountManager } from '@/hooks/use-account-manager';
import { RxPageShell } from '@/components/rx';
import {
  formatCents,
  useRxAffiliateBalance,
  useRxAffiliateTerms,
  useRxFeeSchedule,
  useRxSetAffiliateTerms,
  useRxSetNetworkFeeSchedule,
  type RxAffiliateTermsBasis,
  type RxStoreFeeSchedule,
} from '@/lib/api/rx';

const BASIS_OPTIONS: { value: RxAffiliateTermsBasis; labelKey: string }[] = [
  { value: 'order_subtotal_bps', labelKey: 'affiliateTerms.basisOptions.orderSubtotalBps' },
  { value: 'order_net_bps', labelKey: 'affiliateTerms.basisOptions.orderNetBps' },
  { value: 'flat_per_case_minor', labelKey: 'affiliateTerms.basisOptions.flatPerCaseMinor' },
  { value: 'margin_keep_bps', labelKey: 'affiliateTerms.basisOptions.marginKeepBps' },
];

const formatValue = (
  basis: RxAffiliateTermsBasis,
  value: number,
  currency: string | null,
  t: (key: string, params?: Record<string, unknown>) => string,
): string => {
  if (basis === 'flat_per_case_minor') {
    return t('affiliateTerms.flatPerCaseValue', {
      amount: (value / 100).toFixed(2),
      currency: currency ?? 'USD',
    });
  }
  return `${(value / 100).toFixed(2)}%`;
};

/**
 * "What you pay" — the costs Tagada passes through at cost: clinical
 * encounter fees (network-wide) and per-medication pharmacy costs.
 * Staff can edit the network fee schedule inline.
 */
function WhatYouPayCard({
  data,
  isAccountManager,
  onSaved,
}: {
  data: RxStoreFeeSchedule;
  isAccountManager: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const setSchedule = useRxSetNetworkFeeSchedule();
  const fs = data.feeSchedule;
  const currency = fs?.currency ?? 'USD';
  const [editing, setEditing] = React.useState(false);
  const toDollars = (minor: number | null | undefined) =>
    minor != null ? (minor / 100).toFixed(2) : '';
  const [initial, setInitial] = React.useState(toDollars(fs?.initialVisitMinor));
  const [followUp30, setFollowUp30] = React.useState(toDollars(fs?.followUpVisitMinor?.['30']));
  const [followUp90, setFollowUp90] = React.useState(toDollars(fs?.followUpVisitMinor?.['90']));

  const parse = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number.parseFloat(v);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
  };

  const save = async () => {
    if (!data.network) return;
    try {
      await setSchedule.mutateAsync({
        slug: data.network.slug,
        feeSchedule: {
          currency,
          initialVisitMinor: parse(initial),
          followUpVisitMinor: {
            ...(parse(followUp30) != null ? { '30': parse(followUp30) as number } : {}),
            ...(parse(followUp90) != null ? { '90': parse(followUp90) as number } : {}),
          },
          ...(fs?.extendedFollowUpMinor
            ? { extendedFollowUpMinor: fs.extendedFollowUpMinor }
            : {}),
        },
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      toast({
        title: t('affiliateTerms.whatYouPay.toastSaveFailedTitle'),
        description: err instanceof Error ? err.message : t('affiliateTerms.whatYouPay.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const feeRow = (label: string, minor: number | null | undefined) => (
    <div className="flex items-center justify-between text-sm">
      <span>{label}</span>
      <span className="font-semibold">
        {minor != null ? (
          formatCents(minor, currency)
        ) : (
          <span className="text-muted-foreground text-xs font-normal">{t('affiliateTerms.whatYouPay.notConfigured')}</span>
        )}
      </span>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{t('affiliateTerms.whatYouPay.title')}</CardTitle>
            <CardDescription>
              {t('affiliateTerms.whatYouPay.description')}
              {data.network
                ? t('affiliateTerms.whatYouPay.clinicianNetwork', { network: data.network.displayName })
                : ''}
            </CardDescription>
          </div>
          {isAccountManager && data.network ? (
            <Button variant="outline" size="sm" onClick={() => setEditing((e) => !e)}>
              {editing ? t('affiliateTerms.whatYouPay.cancelEdit') : t('affiliateTerms.whatYouPay.editFees')}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">{t('affiliateTerms.whatYouPay.initialConsultationLabel', { currency })}</Label>
              <Input value={initial} onChange={(e) => setInitial(e.target.value)} inputMode="decimal" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('affiliateTerms.whatYouPay.followUpMonthlyLabel', { currency })}</Label>
              <Input value={followUp30} onChange={(e) => setFollowUp30(e.target.value)} inputMode="decimal" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('affiliateTerms.whatYouPay.followUp90Label', { currency })}</Label>
              <Input value={followUp90} onChange={(e) => setFollowUp90(e.target.value)} inputMode="decimal" />
            </div>
            <div className="md:col-span-3">
              <Button size="sm" onClick={() => void save()} disabled={setSchedule.isPending}>
                {setSchedule.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('affiliateTerms.whatYouPay.saveFeeSchedule')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('affiliateTerms.whatYouPay.consultationsHeading')}
            </div>
            {feeRow(t('affiliateTerms.whatYouPay.initialConsultationRow'), fs?.initialVisitMinor)}
            {fs?.followUpVisitMinor?.['30'] != null
              ? feeRow(t('affiliateTerms.whatYouPay.followUpMonthlyRow'), fs.followUpVisitMinor['30'])
              : null}
            {fs?.followUpVisitMinor?.['90'] != null
              ? feeRow(t('affiliateTerms.whatYouPay.followUp90Row'), fs.followUpVisitMinor['90'])
              : null}
            {fs?.extendedFollowUpMinor?.['90'] != null
              ? feeRow(t('affiliateTerms.whatYouPay.extendedCareRow'), fs.extendedFollowUpMinor['90'])
              : null}
          </div>
        )}

        {data.medications.length > 0 ? (
          <div className="space-y-2 border-t pt-3">
            <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('affiliateTerms.whatYouPay.medicationHeading')}
            </div>
            {data.medications.map((m) => (
              <div key={m.offeringId} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">{m.name}</span>
                <span className="whitespace-nowrap font-semibold">
                  {m.medicationCostMinor != null ? (
                    formatCents(m.medicationCostMinor, m.costCurrency ?? 'USD')
                  ) : (
                    <span className="text-muted-foreground text-xs font-normal">
                      {t('affiliateTerms.whatYouPay.notConfigured')}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function RxAffiliateTermsPage() {
  const { t } = useTranslation('rx');
  const { id: storeId } = useStoreNavigate();
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const termsQuery = useRxAffiliateTerms(storeId);
  const balanceQuery = useRxAffiliateBalance(storeId);
  const feeScheduleQuery = useRxFeeSchedule(storeId);
  const setTerms = useRxSetAffiliateTerms();

  const [basis, setBasis] = React.useState<RxAffiliateTermsBasis>('order_subtotal_bps');
  const [value, setValue] = React.useState('2000');
  const [currency, setCurrency] = React.useState('USD');
  const [label, setLabel] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const handleCreate = async () => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast({
        title: t('affiliateTerms.toasts.invalidValueTitle'),
        description:
          basis === 'flat_per_case_minor'
            ? t('affiliateTerms.toasts.invalidValueFlat')
            : t('affiliateTerms.toasts.invalidValueDefault'),
        variant: 'destructive',
      });
      return;
    }
    try {
      await setTerms.mutateAsync({
        storeId,
        basis,
        value: parsed,
        currency: basis === 'flat_per_case_minor' ? currency : undefined,
        label: label.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast({
        title: t('affiliateTerms.toasts.commissionUpdatedTitle'),
        description: t('affiliateTerms.toasts.commissionUpdatedDescription'),
      });
      termsQuery.refetch();
      setLabel('');
      setNotes('');
    } catch (err) {
      toast({
        title: t('affiliateTerms.toasts.saveTermsFailedTitle'),
        description: err instanceof Error ? err.message : t('affiliateTerms.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const rows = termsQuery.data ?? [];
  const active = rows.find((row) => row.status === 'active') ?? null;
  const history = rows
    .filter((row) => row.status !== 'active')
    .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime());

  const balances = balanceQuery.data?.balances ?? [];
  const ledgerEntries = balanceQuery.data?.entries ?? [];

  return (
    <RxPageShell
      title={t('affiliateTerms.title')}
      description={t('affiliateTerms.description')}
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('affiliateTerms.balance.title')}</CardTitle>
          <CardDescription>
            {t('affiliateTerms.balance.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {balanceQuery.isLoading ? (
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          ) : balances.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('affiliateTerms.balance.empty')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-6">
              {balances.map((b) => (
                <div key={b.currency}>
                  <div className="text-3xl font-bold text-emerald-700">
                    {formatCents(b.balanceMinor, b.currency)}
                  </div>
                  <div className="text-muted-foreground text-xs">{t('affiliateTerms.balance.pendingPayout', { currency: b.currency })}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {feeScheduleQuery.data ? (
        <WhatYouPayCard
          data={feeScheduleQuery.data}
          isAccountManager={isAccountManager}
          onSaved={() => {
            void feeScheduleQuery.refetch();
          }}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('affiliateTerms.activeTerms.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {termsQuery.isLoading ? (
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          ) : active ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                  {t('affiliateTerms.activeTerms.activeBadge')}
                </Badge>
                <span className="font-semibold">{active.label ?? t('affiliateTerms.activeTerms.fallbackLabel')}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div>
                  <div className="text-muted-foreground">{t('affiliateTerms.activeTerms.basis')}</div>
                  <div>
                    {(() => {
                      const opt = BASIS_OPTIONS.find((o) => o.value === active.basis);
                      return opt ? t(opt.labelKey) : active.basis;
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('affiliateTerms.activeTerms.value')}</div>
                  <div className="font-semibold">
                    {formatValue(active.basis, active.value, active.currency, t)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('affiliateTerms.activeTerms.effectiveFrom')}</div>
                  <div>{new Date(active.effectiveFrom).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('affiliateTerms.activeTerms.notes')}</div>
                  <div className="truncate">{active.notes ?? '—'}</div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              {isAccountManager
                ? t('affiliateTerms.activeTerms.emptyStaff')
                : t('affiliateTerms.activeTerms.emptyMerchant')}
            </p>
          )}
          {!isAccountManager ? (
            <p className="text-muted-foreground mt-4 text-xs">
              {t('affiliateTerms.activeTerms.merchantHint')}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {isAccountManager ? (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4" /> {t('affiliateTerms.setNewTerms.title')}
          </CardTitle>
          <CardDescription>
            {t('affiliateTerms.setNewTerms.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('affiliateTerms.setNewTerms.basisLabel')}</Label>
              <Select value={basis} onValueChange={(v) => setBasis(v as RxAffiliateTermsBasis)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('affiliateTerms.setNewTerms.valueLabel')}</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                {basis === 'flat_per_case_minor'
                  ? t('affiliateTerms.setNewTerms.valueHintFlat')
                  : basis === 'margin_keep_bps'
                    ? t('affiliateTerms.setNewTerms.valueHintMargin')
                    : t('affiliateTerms.setNewTerms.valueHintDefault')}
              </p>
            </div>
            {basis === 'flat_per_case_minor' ? (
              <div className="space-y-2">
                <Label>{t('affiliateTerms.setNewTerms.currencyLabel')}</Label>
                <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label>{t('affiliateTerms.setNewTerms.labelLabel')}</Label>
              <Input
                placeholder={t('affiliateTerms.setNewTerms.labelPlaceholder')}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>{t('affiliateTerms.setNewTerms.notesLabel')}</Label>
              <Input
                placeholder={t('affiliateTerms.setNewTerms.notesPlaceholder')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={setTerms.isPending}>
            {setTerms.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('affiliateTerms.setNewTerms.saveActivate')}
          </Button>
        </CardContent>
      </Card>
      ) : null}

      {history.length > 0 ? (
        <TableCard>
          <div className="text-sm font-medium px-1 pb-2">{t('affiliateTerms.history.heading')}</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('affiliateTerms.history.columns.label')}</TableHead>
                <TableHead>{t('affiliateTerms.history.columns.basis')}</TableHead>
                <TableHead>{t('affiliateTerms.history.columns.value')}</TableHead>
                <TableHead>{t('affiliateTerms.history.columns.activeRange')}</TableHead>
                <TableHead>{t('affiliateTerms.history.columns.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.label ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {(() => {
                      const opt = BASIS_OPTIONS.find((o) => o.value === row.basis);
                      return opt ? t(opt.labelKey) : row.basis;
                    })()}
                  </TableCell>
                  <TableCell>{formatValue(row.basis, row.value, row.currency, t)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(row.effectiveFrom).toLocaleDateString()} →{' '}
                    {row.effectiveTo ? new Date(row.effectiveTo).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {row.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      ) : null}

      {ledgerEntries.length > 0 ? (
        <TableCard>
          <div className="px-1 pb-2 text-sm font-medium">{t('affiliateTerms.activity.heading')}</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('affiliateTerms.activity.columns.date')}</TableHead>
                <TableHead>{t('affiliateTerms.activity.columns.case')}</TableHead>
                <TableHead>{t('affiliateTerms.activity.columns.detail')}</TableHead>
                <TableHead className="text-right">{t('affiliateTerms.activity.columns.amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledgerEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(entry.postedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{entry.sourceId ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[320px] truncate text-xs">
                    {entry.memo ?? '—'}
                  </TableCell>
                  <TableCell
                    className={
                      entry.amountMinor >= 0
                        ? 'text-right font-semibold text-emerald-700'
                        : 'text-right font-semibold text-red-600'
                    }
                  >
                    {entry.amountMinor >= 0 ? '+' : ''}
                    {formatCents(entry.amountMinor, entry.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      ) : null}
    </RxPageShell>
  );
}
